import Ajv from "ajv";
import axios from "axios";
import axiosRetry from 'axios-retry';
import urlJoin from "url-join";
import Config from "../../../core/config/Config";
import LanguageModel from "../../../core/language/model/LanguageModel";
import NGTConfigKey from "../../../ngt/base/NGTConfigKey";
import CharacterModel from "../../characters/model/CharacterModel";
import LlmConversationMessage from "../../conversation/model/LlmConversationMessage";
import SessionModel from "../../session/model/SessionModel";
import BackendErrorEvent from "../event/BackendErrorEvent";
import ConfigDto from "../model/ConfigDto";

export default class BackendService {
    /**
     * @constructor
     * @param {soma.EventDispatcher} dispatcher 
     * @param {Config} config 
     * @param {SessionModel} sessionModel 
     * @param {LanguageModel} languageModel 
     */
    constructor(dispatcher, config, sessionModel, languageModel) {
        this._dispatcher = dispatcher;
        this._config = config;
        this._sessionModel = sessionModel;
        this._languageModel = languageModel;

        this._endpoint = this._config.getValue(NGTConfigKey.BACKEND_ENDPOINT);
        this._apiVersion = this._config.getValue(NGTConfigKey.BACKEND_API_VERSION);
        this._appVersion = this._config.getValue(NGTConfigKey.APP_VERSION);
        this._apiKey = this._config.getValue(NGTConfigKey.BACKEND_API_KEY);
    }

    /**
     * Overrides api version, useful for testing.
     * @param {string} apiVersion 
     */
    overrideApiVersion(apiVersion) {
        this._apiVersion = apiVersion;
    }

    /**
     * 
     * @param {Object} params
     * @param {string} params.chapterId 
     * @param {string} params.conversationId 
     * @param {string} params.phaseId 
     * @param {Array<LlmConversationMessage>} params.conversationHistory for now only the last user message. 
     * @returns {Promise<{phase: string?, finish: boolean, outcome: string?, trackerUpdates: Array<{character: string, trackerDeltas: object}>, reasoning: string}>}
     */
    async decideNextPhase({ chapterId, conversationId, phaseId, conversationHistory }) {
        const trackerUpdateSchema = {
            "type": "object",
            "properties": {
                "character": { "type": "string" },
                "trackerDeltas": { "type": "object" },
            },
            "required": ["character", "trackerDeltas"],
        };

        const rootSchema = {
            "type": "object",
            "properties": {
                "phase": { "type": ["string", "null"] },
                "finish": { "type": "boolean" },
                "outcome": { "type": ["string", "null"] },
                "trackerUpdates": {
                    "type": "array",
                    "items": trackerUpdateSchema,
                },
                "reasoning": { "type": "string" }
            },
            "required": ["finish"],
        };

        return await this._sendPostRequest('lock-and-key', {
            chapter_id: chapterId,
            conversation_id: conversationId,
            phase_id: phaseId,
            conversation_history: conversationHistory.map(message => message.toObject()),
        }, { expectedResponseSchema: rootSchema });
    }

    /**
     * 
     * @param {Object} params
     * @param {string} params.chapterId 
     * @param {string} params.conversationId 
     * @param {string} params.phaseId 
     * @param {number} params.turnsInThisPhase
     * @param {Array<LlmConversationMessage>} params.conversationHistory pass the whole conversation history
     * @param {Array<CharacterModel>} params.characters
     * @returns {Promise<{characters: Array<{action: string, dialogue: string, name: string}>}>}
     */
    async roleplay({ chapterId, conversationId, phaseId, turnsInThisPhase, conversationHistory, characters }) {
        const characterSchema = {
            "type": "object",
            "properties": {
                "name": { "type": "string" },
                "action": { "type": "string" },
                "dialogue": { "type": "string" },
                "emotion": { "type": "string" },
            },
            "required": ["name", "action", "dialogue", "emotion"],
        };

        const rootSchema = {
            "type": "object",
            "properties": {
                "characters": {
                    "type": "array",
                    "items": characterSchema,
                },
            },
            "required": ["characters"],
        };

        return await this._sendPostRequest('roleplay', {
            chapter_id: chapterId,
            conversation_id: conversationId,
            phase_id: phaseId,
            turns_in_phase: turnsInThisPhase,
            conversation_history: conversationHistory.map(message => message.toObject()),
            characters: characters.map(character => character.toObject()),
            language: this._languageModel.currentLanguage,
        }, { expectedResponseSchema: rootSchema });
    }

    /**
     * 
     * @param {Object} params
     * @param {string} params.chapterId 
     * @param {string} params.conversationId 
     * @param {string} params.phaseId 
     * @param {number} params.turnsInThisPhase
     * @param {Array<LlmConversationMessage>} params.conversationHistory pass the whole conversation history
     * @param {Array<CharacterModel>} params.characters
     * @returns {Promise<{characters: Array<{action: string, dialogue: string, name: string}>}>}
     */
    async roleplayEndOfConversation({ chapterId, conversationId, phaseId, turnsInThisPhase, conversationHistory, characters }) {
        const characterSchema = {
            "type": "object",
            "properties": {
                "name": { "type": "string" },
                "action": { "type": "string" },
                "dialogue": { "type": "string" },
                "emotion": { "type": "string" },
            },
            "required": ["name", "action", "dialogue", "emotion"],
        };

        const rootSchema = {
            "type": "object",
            "properties": {
                "characters": {
                    "type": "array",
                    "items": characterSchema,
                },
            },
            "required": ["characters"],
        };

        return await this._sendPostRequest('roleplay-last-turn', {
            chapter_id: chapterId,
            conversation_id: conversationId,
            phase_id: phaseId,
            turns_in_phase: turnsInThisPhase,
            conversation_history: conversationHistory.map(message => message.toObject()),
            characters: characters.map(character => character.toObject()),
            language: this._languageModel.currentLanguage,
        }, { expectedResponseSchema: rootSchema });
    }

    /**
     * 
     * @param {Object} params
     * @param {string} params.chapterId 
     * @param {string} params.conversationId
     * @param {Array<LlmConversationMessage>} params.conversationHistory pass the whole conversation history
     * @returns {Promise<{feedback: Array<{quote: string, feedback: string}>, summary: string}>}
     */
    async conversationFeedback({ chapterId, conversationId, conversationHistory }) {
        return await this._sendPostRequest('conversation-feedback', {
            chapter_id: chapterId,
            conversation_id: conversationId,
            conversation_history: conversationHistory.map(message => message.toObject()),
            language: this._languageModel.currentLanguage,
        });
    }

    /**
     * 
     * @param {Object} params
     * @param {string} params.chapterId 
     * @param {string} params.conversationId
     * @param {Array<LlmConversationMessage>} params.conversationHistory pass the whole conversation history
     * @param {string} params.playerName
     * @returns {Promise<{message: string, finished: boolean, summary: string}>}
     */
    async endOfConversationReflection({ chapterId, conversationId, conversationHistory, playerName }) {
        return await this._sendPostRequest('conversation-reflection-last-turn', {
            chapter_id: chapterId,
            conversation_id: conversationId,
            conversation_history: conversationHistory.map(message => message.toObject()),
            player_name: playerName,
            language: this._languageModel.currentLanguage,
        });
    }

    /**
     * 
     * @param {Object} params
     * @param {string} params.chapterId 
     * @param {string} params.conversationId
     * @param {Array<LlmConversationMessage>} params.conversationHistory pass the whole conversation history
     * @param {string} params.playerName
     * @returns {Promise<{message: string, finished: boolean, summary: string}>}
     */
    async conversationReflection({ chapterId, conversationId, conversationHistory, playerName }) {
        return await this._sendPostRequest('conversation-reflection', {
            chapter_id: chapterId,
            conversation_id: conversationId,
            conversation_history: conversationHistory.map(message => message.toObject()),
            player_name: playerName,
            language: this._languageModel.currentLanguage,
        });
    }

    async getConfig() {
        const data = await this._sendGetRequest('config', null, { failSilently: true });
        return new ConfigDto(data);
    }

    /**
     * 
     * @param {Object} params
     * @param {string[]} params.feedbackSummaries 
     * @param {string[]} params.reflectionSummaries
     * @returns {Promise<{message: string}>}
     */
    async reportPerformance({ feedbackSummaries, reflectionSummaries }) {
        return await this._sendPostRequest('report/performance', {
            feedback_summaries: feedbackSummaries,
            reflection_summaries: reflectionSummaries,
            language: this._languageModel.currentLanguage,
        });
    }

    /**
     * 
     * @param {Object} params
     * @param {string[]} params.feedbackSummaries 
     * @param {string[]} params.reflectionSummaries
     * @returns {Promise<{tips: Array<{ title: string, content: string}>}>}
     */
    async reportTips({ feedbackSummaries, reflectionSummaries }) {
        return await this._sendPostRequest('report/tips', {
            feedback_summaries: feedbackSummaries,
            reflection_summaries: reflectionSummaries,
            language: this._languageModel.currentLanguage,
        });
    }

    /**
     * @param {string} endpointUrl 
     * @param {Object} requestBody
     * @param {Object} params
     * @param {*} params.expectedResponseSchema
     * @param {bool} params.failSilently
     */
    async _sendGetRequest(endpointUrl, requestBody, { expectedResponseSchema, failSilently } = {}) {
        try {
            const client = this._createClient({ expectedResponseSchema, failSilently });
            const response = await client.get(endpointUrl, requestBody);
            return response.data;
        } catch (error) {
            console.error(`[${BackendService.name}] Endpoint: ${endpointUrl}`, error);
        }
    }

    /**
     * @param {string} endpointUrl 
     * @param {Object} requestBody
     * @param {Object} params
     * @param {*} params.expectedResponseSchema
     * @param {bool} params.failSilently
     */
    async _sendPostRequest(endpointUrl, requestBody, { expectedResponseSchema, failSilently } = {}) {
        try {
            const client = this._createClient({ expectedResponseSchema, failSilently });
            const response = await client.post(endpointUrl, requestBody);
            return response.data;
        } catch (error) {
            console.error(`[${BackendService.name}] Endpoint: ${endpointUrl}`, error);
            throw error;
        }
    }

    _createClient({ expectedResponseSchema, failSilently } = {}) {
        const client = axios.create({
            baseURL: urlJoin(this._endpoint, this._apiVersion),
            headers: {
                'API-KEY': this._apiKey,
                'SESSION-ID': this._sessionModel.sessionId,
                'APP-VERSION': this._appVersion,
            },
        });

        axiosRetry(client, {
            retries: 3,
            retryDelay: axiosRetry.exponentialDelay,
            onRetry: (retryCount, error) => {
                console.warn(`[${BackendService.name}] Retry attempt ${retryCount}`, error);
            },
            onMaxRetryTimesExceeded: (error, retryCount) => {
                console.error(`[${BackendService.name}] Max retry times (${retryCount}) exceeded`, error);

                const backendErrorEvent = new BackendErrorEvent(BackendErrorEvent.ERROR, error, failSilently);
                this._dispatcher.dispatch(backendErrorEvent.type, { data: backendErrorEvent });
            },
            retryCondition: (error) => {
                // Content filter error - do not retry.
                if (error.response.status === 400 && error.response?.data?.detail?.error?.code === `content_filter`) {
                    const backendErrorEvent = new BackendErrorEvent(BackendErrorEvent.CONTENT_FILTER_VIOLATION, error, failSilently);
                    this._dispatcher.dispatch(backendErrorEvent.type, { data: backendErrorEvent });
                    return false;
                }

                // Retry for network errors or idempotent requests.
                if (axiosRetry.isNetworkOrIdempotentRequestError(error)) {
                    return true;
                }

                // Schema validation - if the response does not match the schema, retry.
                if (expectedResponseSchema && error.response && error.response.data) {
                    // Validate the response against the schema.
                    const ajv = new Ajv();
                    const validateSchema = ajv.compile(expectedResponseSchema);
                    const isValidSchema = validateSchema(error.response.data);
                    if (!isValidSchema) {
                        console.error(`[${BackendService.name}] Response did not match schema:`, validateSchema.errors);
                        return true; // Retry if the schema validation fails.
                    }
                }

                // Do not retry otherwise.
                const backendErrorEvent = new BackendErrorEvent(BackendErrorEvent.ERROR, error, failSilently);
                this._dispatcher.dispatch(backendErrorEvent.type, { data: backendErrorEvent });
                return false;
            },
            validateResponse: (response) => {
                // Work-around: to make retry on succesful requests work, we need to validate the schema here as well.
                // If you return true here, the retryCondition will be ignored, so we return false if the schema is invalid and check it again in the retryCondition.
                if (expectedResponseSchema) {
                    // Validate the response against the schema.
                    const ajv = new Ajv();
                    const validateSchema = ajv.compile(expectedResponseSchema);
                    const isValidSchema = validateSchema(response.data);
                    if (!isValidSchema) {
                        console.error(`[${BackendService.name}] Response did not match schema:`, validateSchema.errors);
                        return false; // Consider the response as invalid if the schema validation fails. This will trigger 'retryCondition'.
                    }
                }

                // 2XX responses are valid.
                const status = response.status;
                if (status >= 200 && status < 300) {
                    return true;
                }

                return false;
            }
        });

        return client;
    }
}

export const HTTPMethod = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE',
    PATCH: 'PATCH',
};